import { captureException } from '@sentry/nextjs';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { colorManagerAtom, uiColors } from '../../store/uiStore';

type Colors = {
  name: string;
  background: string;
  text: string;
  navActive?: boolean;
};

interface ColorSwitcherProps {
  context: { collectionId: number } | 'wallet';
  returnNormalColor?: boolean;
}

//localstorage keys
const COLLECTION_COLORS = 'collectionColors';
const WALLET_COLOR = 'walletColor';

const ColorSwitcher = ({ context, returnNormalColor = true }: ColorSwitcherProps) => {
  const collectionId =
    typeof context === 'object' && 'collectionId' in context ? context.collectionId : null;
  const isWallet = context === 'wallet';

  const [colorManager, setColorManager] = useAtom(colorManagerAtom);
  const [dropdown, setDropdown] = useState(false);

  const colors = useMemo<Colors[]>(() => uiColors, []);

  const changeColor = (color) => {
    setColorManager(
      colorManager.background === color.background
        ? { background: 'bg-black', text: 'text-white' }
        : { ...colorManager, ...color }
    );
    setDropdown(false);
    // save to local storage
    const collectionColors = getLocalStorageColors();
    if (collectionId) {
      collectionColors[collectionId] = color;
      localStorage.setItem(COLLECTION_COLORS, JSON.stringify(collectionColors));
    } else if (isWallet) {
      localStorage.setItem(WALLET_COLOR, JSON.stringify(color));
    }
  };

  // on unmount return to normal color
  useEffect(() => {
    // on load check if we have a color for this collection in local storage
    if (collectionId && returnNormalColor) {
      try {
        const collectionColors = getLocalStorageColors();
        if (collectionColors?.[collectionId]) {
          changeColor(collectionColors[collectionId]);
        }
      } catch (error) {
        captureException(error);
      }
    } else if (isWallet) {
      try {
        const walletColor = localStorage.getItem(WALLET_COLOR);
        if (walletColor) changeColor(JSON.parse(walletColor));
      } catch (error) {
        captureException(error);
      }
    }

    if (returnNormalColor) {
      return () => {
        setColorManager({
          ...colorManager,
          background: 'bg-black',
          text: 'text-white',
          navActive: false,
        });
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLocalStorageColors = () => {
    try {
      const storedData = localStorage.getItem(COLLECTION_COLORS);
      if (!storedData) return {};
      const collectionColors = JSON.parse(storedData);
      return collectionColors;
    } catch (error) {
      return null;
    }
  };

  return (
    <div
      className="relative h-[22px] grid cursor-pointer grid-cols-[min-content_min-content] overflow-visible z-20"
      onClick={() => (!dropdown ? setDropdown(!dropdown) : null)}
    >
      <div className="grid gap-[8px]">
        {colors.map((color) => {
          const newColor = {
            background: color.background,
            text: color.text,
            navActive: !!color.navActive,
            name: color.name,
          };

          return (
            <div
              key={color.background}
              onClick={() => (!dropdown ? setDropdown(!dropdown) : changeColor(newColor))}
              className={clsx([
                colorManager.background === color.background || dropdown ? 'flex' : 'hidden',
                'rounded-full w-[22px] h-[22px] cursor-pointer border',
                color.background,
                colorManager.text === 'text-black' ? 'border-black' : 'border-white',
              ])}
            ></div>
          );
        })}
      </div>
      <div
        className="p-[7px] cursor-pointer"
        onClick={() => (dropdown ? setDropdown(!dropdown) : null)}
      >
        <svg
          width="14"
          height="8"
          viewBox="0 0 14 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={() => (dropdown ? setDropdown(!dropdown) : null)}
          className={clsx([dropdown ? 'rotate-180' : ''])}
        >
          <path d="M1 1L7 7L13 1" stroke={colorManager.text === 'text-black' ? 'black' : 'white'} />
        </svg>
      </div>
    </div>
  );
};

export default ColorSwitcher;
